import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Box,
  Button,
  TextField,
  MenuItem,
  Chip,
  LinearProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import ChatIcon from "@material-ui/icons/Chat";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import WarningIcon from "@material-ui/icons/Warning";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  metricCard: {
    padding: theme.spacing(2),
    borderRadius: 12,
    height: "100%",
    position: "relative",
  },
  metricValue: {
    fontSize: "1.8rem",
    fontWeight: "bold",
  },
  metricLabel: {
    fontSize: "0.75rem",
    color: "#666",
    textTransform: "uppercase",
  },
  tableSection: {
    marginTop: theme.spacing(3),
  },
  tableHeader: {
    backgroundColor: "#1a5c1a",
    "& th": { color: "#fff", fontWeight: 600 },
  },
  lossReasonChip: {
    margin: 2,
    fontSize: "0.7rem",
  },
}));

const ProductivityReport = () => {
  const classes = useStyles();
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    try {
      const { data } = await api.get(`/reports/productivity?period=${period}`);
      setData(data);
    } catch (err) {
      // Sem dados cadastrados
      setData({ summary: { totalAtendimentos: 0, tempoMedioResposta: "0s", taxaConversao: 0, ticketsResolvidos: 0, ticketsAbertos: 0 }, agents: [], lossReasons: [] });
    }
  };

  if (!data) return <Typography>Carregando...</Typography>;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Relatório de Produtividade
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Desempenho da equipe de atendimento e vendas
          </Typography>
        </div>
        <TextField
          select
          size="small"
          variant="outlined"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          style={{ width: 150 }}
        >
          <MenuItem value="today">Hoje</MenuItem>
          <MenuItem value="week">Esta Semana</MenuItem>
          <MenuItem value="month">Este Mês</MenuItem>
          <MenuItem value="quarter">Trimestre</MenuItem>
        </TextField>
      </div>

      {/* Cards de Métricas */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper className={classes.metricCard} style={{ borderLeft: "4px solid #2196f3" }}>
            <ChatIcon style={{ color: "#2196f3", opacity: 0.3, position: "absolute", right: 12, top: 12, fontSize: 36 }} />
            <Typography className={classes.metricValue} style={{ color: "#2196f3" }}>
              {data.summary.totalAtendimentos}
            </Typography>
            <Typography className={classes.metricLabel}>Atendimentos</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper className={classes.metricCard} style={{ borderLeft: "4px solid #ff9800" }}>
            <AccessTimeIcon style={{ color: "#ff9800", opacity: 0.3, position: "absolute", right: 12, top: 12, fontSize: 36 }} />
            <Typography className={classes.metricValue} style={{ color: "#ff9800" }}>
              {data.summary.tempoMedioResposta}
            </Typography>
            <Typography className={classes.metricLabel}>Tempo Médio Resposta</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper className={classes.metricCard} style={{ borderLeft: "4px solid #4caf50" }}>
            <CheckCircleIcon style={{ color: "#4caf50", opacity: 0.3, position: "absolute", right: 12, top: 12, fontSize: 36 }} />
            <Typography className={classes.metricValue} style={{ color: "#4caf50" }}>
              {data.summary.ticketsResolvidos}
            </Typography>
            <Typography className={classes.metricLabel}>Resolvidos</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper className={classes.metricCard} style={{ borderLeft: "4px solid #9c27b0" }}>
            <TrendingUpIcon style={{ color: "#9c27b0", opacity: 0.3, position: "absolute", right: 12, top: 12, fontSize: 36 }} />
            <Typography className={classes.metricValue} style={{ color: "#9c27b0" }}>
              {data.summary.taxaConversao}%
            </Typography>
            <Typography className={classes.metricLabel}>Taxa Conversão</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper className={classes.metricCard} style={{ borderLeft: "4px solid #f44336" }}>
            <WarningIcon style={{ color: "#f44336", opacity: 0.3, position: "absolute", right: 12, top: 12, fontSize: 36 }} />
            <Typography className={classes.metricValue} style={{ color: "#f44336" }}>
              {data.summary.ticketsAbertos}
            </Typography>
            <Typography className={classes.metricLabel}>Em Aberto</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabela de Agentes */}
      <div className={classes.tableSection}>
        <Typography variant="h6" gutterBottom style={{ fontWeight: 600 }}>
          Desempenho por Atendente
        </Typography>
        <TableContainer component={Paper} style={{ borderRadius: 12 }}>
          <Table>
            <TableHead className={classes.tableHeader}>
              <TableRow>
                <TableCell>Atendente</TableCell>
                <TableCell align="center">Atendimentos</TableCell>
                <TableCell align="center">Tempo Médio</TableCell>
                <TableCell align="center">Resolvidos</TableCell>
                <TableCell align="center">Conversão</TableCell>
                <TableCell align="center">Satisfação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.agents.map((agent) => (
                <TableRow key={agent.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Avatar style={{ width: 32, height: 32, backgroundColor: "#2e7d32" }}>
                        {agent.name.charAt(0)}
                      </Avatar>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {agent.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={agent.atendimentos} size="small" color="primary" />
                  </TableCell>
                  <TableCell align="center">{agent.tempoMedio}</TableCell>
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 4 }}>
                      <Typography variant="body2">{agent.resolvidos}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        ({((agent.resolvidos / agent.atendimentos) * 100).toFixed(0)}%)
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" style={{ fontWeight: 600, color: "#2e7d32" }}>
                      {agent.conversao}%
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${agent.satisfacao}/5`}
                      size="small"
                      style={{
                        backgroundColor: agent.satisfacao >= 4.5 ? "#e8f5e9" : agent.satisfacao >= 4 ? "#fff3e0" : "#ffebee",
                        color: agent.satisfacao >= 4.5 ? "#2e7d32" : agent.satisfacao >= 4 ? "#e65100" : "#c62828",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Motivos de Perda */}
      <div className={classes.tableSection}>
        <Typography variant="h6" gutterBottom style={{ fontWeight: 600 }}>
          Motivos de Perda (por que não fechou)
        </Typography>
        <TableContainer component={Paper} style={{ borderRadius: 12 }}>
          <Table size="small">
            <TableHead>
              <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                <TableCell>Motivo</TableCell>
                <TableCell align="center">Ocorrências</TableCell>
                <TableCell align="center">%</TableCell>
                <TableCell>Representação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.lossReasons.map((reason, idx) => (
                <TableRow key={idx}>
                  <TableCell style={{ fontWeight: 500 }}>{reason.reason}</TableCell>
                  <TableCell align="center">{reason.count}</TableCell>
                  <TableCell align="center">{reason.percent}%</TableCell>
                  <TableCell style={{ width: 200 }}>
                    <LinearProgress
                      variant="determinate"
                      value={reason.percent}
                      style={{ height: 8, borderRadius: 4 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default ProductivityReport;
